import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';
import { createClient } from '@supabase/supabase-js';
import Navigation from '../../components/Navigation';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

const MarketplacePage = () => {
  const [activeTab, setActiveTab] = useState('applications');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [purchaseStep, setPurchaseStep] = useState('details');
  const [marketplaceItems, setMarketplaceItems] = useState<any>({
    applications: [],
    themes: [],
    components: [],
    connectors: []
  });
  const [categories, setCategories] = useState<any>({
    applications: ['All'],
    themes: ['All'],
    components: ['All'],
    connectors: ['All']
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMarketplaceData();
  }, []);

  const loadMarketplaceData = async () => {
    try {
      setLoading(true);
      
      // Load marketplace items
      const { data: items, error: itemsError } = await supabase
        .from('marketplace_items')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true, nullsFirst: false });

      if (itemsError) throw itemsError;

      // Load categories
      const { data: cats, error: catsError } = await supabase
        .from('marketplace_categories')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true, nullsFirst: false });

      if (catsError) throw catsError;

      // Organize items by type
      const organizedItems: any = {
        applications: [],
        themes: [],
        components: [],
        connectors: []
      };

      items?.forEach((item: any) => {
        const type = item.type?.toLowerCase() || 'applications';
        if (organizedItems[type]) {
          organizedItems[type].push(item);
        }
      });

      setMarketplaceItems(organizedItems);

      // Organize categories by type
      const organizedCats: any = {
        applications: ['All'],
        themes: ['All'],
        components: ['All'],
        connectors: ['All']
      };

      cats?.forEach((cat: any) => {
        const type = cat.type?.toLowerCase() || 'applications';
        if (organizedCats[type] && !organizedCats[type].includes(cat.name)) {
          organizedCats[type].push(cat.name);
        }
      });

      setCategories(organizedCats);
    } catch (error) {
      console.error('Error loading marketplace data:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'applications', name: 'Application Library', icon: 'ri-apps-2-line' },
    { id: 'themes', name: 'UI Themes', icon: 'ri-palette-line' },
    { id: 'components', name: 'Platform Components', icon: 'ri-puzzle-line' },
    { id: 'connectors', name: 'Connectors', icon: 'ri-plug-line' }
  ];

  const filteredItems = marketplaceItems[activeTab as keyof typeof marketplaceItems].filter((item: any) => {
    const matchesSearch = item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'all' ||
                           item.category?.toLowerCase() === selectedCategory.toLowerCase();
    return matchesSearch && matchesCategory;
  });

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Marketplace - Applications, Themes, Components & Connectors",
    "description": "Discover and download applications, UI themes, platform components, and connectors to enhance your development workflow.",
    "url": `${import.meta.env.VITE_SITE_URL}/marketplace`
  };

  const handleItemClick = (item: any) => {
    setSelectedItem(item);
    setShowDetailsModal(true);
  };

  const handlePurchase = (item: any) => {
    setSelectedItem(item);
    setShowDetailsModal(false);
    setShowPurchaseModal(true);
    setPurchaseStep('details');
  };

  const handleFreePurchase = (item: any) => {
    setSelectedItem(item);
    setShowDetailsModal(false);
    setShowPurchaseModal(true);
    setPurchaseStep('success');
  };

  const closePurchaseModal = () => {
    setShowPurchaseModal(false);
    setSelectedItem(null);
    setPurchaseStep('details');
  };

  const closeDetailsModal = () => {
    setShowDetailsModal(false);
    setSelectedItem(null);
  };

  const proceedToPayment = () => {
    setPurchaseStep('payment');
  };

  const completePurchase = () => {
    setPurchaseStep('success');
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Marketplace - Applications, Themes, Components & Connectors | Allgo Digital"
        description="Discover and download applications, UI themes, platform components, and connectors to enhance your development workflow."
        keywords="marketplace, applications, UI themes, components, connectors, templates"
        canonical="/marketplace"
        structuredData={structuredData}
      />

      {/* Navigation */}
      <Navigation />

      {/* Back Button */}
      <div className="bg-gray-50 py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-end">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium cursor-pointer">
            <i className="ri-arrow-left-line mr-2"></i>
            Back to Home
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section
        className="relative bg-cover bg-center bg-no-repeat py-20"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://public.readdy.ai/ai/img_res/b7f257d0d83291defd2e166cad484d3c.jpg')`
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Marketplace
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-4xl mx-auto">
            Discover applications, themes, components, and connectors to accelerate your development workflow
          </p>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <input
                type="text"
                placeholder="Search marketplace..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-4 text-lg rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent pl-14"
              />
              <i className="ri-search-line absolute left-5 top-1/2 transform -translate-y-1/2 text-gray-400 text-xl"></i>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8 overflow-x-auto">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  setSelectedCategory('all');
                }}
                className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <i className={`${tab.icon} text-lg`}></i>
                <span>{tab.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap gap-2">
            {categories[activeTab as keyof typeof categories].map((category: string) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category.toLowerCase())}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  selectedCategory === category.toLowerCase()
                    ? 'bg-blue-100 text-blue-700 border border-blue-200'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Results Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">
            {tabs.find(tab => tab.id === activeTab)?.name}
          </h2>
          <div className="text-sm text-gray-500">
            {filteredItems.length} items found
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin"></i>
            <p className="text-gray-600 mt-4">Loading marketplace items...</p>
          </div>
        ) : (
          <>
            {/* Items Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredItems.map((item: any) => (
                <div 
                  key={item.id} 
                  onClick={() => handleItemClick(item)}
                  className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow group cursor-pointer"
                >
                  <div className="aspect-video overflow-hidden">
                    <img
                      src={item.image || 'https://via.placeholder.com/400x300?text=No+Image'}
                      alt={item.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>

                  <div className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors">
                          {item.title}
                        </h3>
                        <p className="text-sm text-gray-500">by {item.author || 'Unknown'}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        item.pricing_model === 'free' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                      }`}>
                        {item.pricing_model === 'free' ? 'Free' : `$${item.price}`}
                      </span>
                    </div>

                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {item.description}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center space-x-1">
                          <i className="ri-star-fill text-yellow-400"></i>
                          <span>{item.rating || '0.0'}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <i className="ri-download-line"></i>
                          <span>{item.downloads || '0'}</span>
                        </div>
                      </div>

                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          item.pricing_model === 'free' ? handleFreePurchase(item) : handlePurchase(item);
                        }}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors whitespace-nowrap"
                      >
                        {item.pricing_model === 'free' ? 'Install' : 'Purchase'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Empty State */}
            {filteredItems.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-search-line text-4xl text-gray-300 mb-4"></i>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No items found</h3>
                <p className="text-gray-500">Try adjusting your search or filter criteria</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-2xl font-bold text-gray-900">
                {selectedItem.title}
              </h3>
              <button
                onClick={closeDetailsModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Image */}
              <div className="aspect-video overflow-hidden rounded-lg mb-6">
                <img
                  src={selectedItem.image || 'https://via.placeholder.com/800x450?text=No+Image'}
                  alt={selectedItem.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Info Bar */}
              <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
                <div className="flex items-center space-x-6">
                  <div>
                    <p className="text-sm text-gray-500">Author</p>
                    <p className="font-medium text-gray-900">{selectedItem.author || 'Unknown'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Category</p>
                    <p className="font-medium text-gray-900">{selectedItem.category}</p>
                  </div>
                  <div className="flex items-center space-x-1">
                    <i className="ri-star-fill text-yellow-400"></i>
                    <span className="font-medium text-gray-900">{selectedItem.rating || '0.0'}</span>
                    <span className="text-sm text-gray-500">({selectedItem.downloads || '0'} downloads)</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-3xl font-bold ${
                    selectedItem.pricing_model === 'free' ? 'text-green-600' : 'text-blue-600'
                  }`}>
                    {selectedItem.pricing_model === 'free' ? 'Free' : `$${selectedItem.price}`}
                  </div>
                  {selectedItem.pricing_model !== 'free' && (
                    <p className="text-sm text-gray-500 capitalize">{selectedItem.pricing_model}</p>
                  )}
                </div>
              </div>

              {/* Description */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Description</h4>
                <p className="text-gray-600 leading-relaxed">{selectedItem.description}</p>
              </div>

              {/* Features */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">What's Included</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center space-x-2">
                    <i className="ri-check-line text-green-500"></i>
                    <span>Full source code and documentation</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <i className="ri-check-line text-green-500"></i>
                    <span>6 months of free updates</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <i className="ri-check-line text-green-500"></i>
                    <span>Email support for 30 days</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <i className="ri-check-line text-green-500"></i>
                    <span>Commercial license included</span>
                  </li>
                </ul>
              </div>

              {/* Requirements */}
              <div className="mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">Requirements</h4>
                <ul className="space-y-2 text-gray-600">
                  <li className="flex items-center space-x-2">
                    <i className="ri-checkbox-circle-line text-blue-500"></i>
                    <span>Allgo Digital Platform account</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <i className="ri-checkbox-circle-line text-blue-500"></i>
                    <span>Compatible with latest platform version</span>
                  </li>
                </ul>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3 pt-6 border-t border-gray-200">
                <button
                  onClick={closeDetailsModal}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  Close
                </button>
                <button
                  onClick={() => selectedItem.pricing_model === 'free' ? handleFreePurchase(selectedItem) : handlePurchase(selectedItem)}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                >
                  {selectedItem.pricing_model === 'free' ? 'Install Now' : 'Purchase Now'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Modal */}
      {showPurchaseModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">
                {purchaseStep === 'details' && 'Purchase Details'}
                {purchaseStep === 'payment' && 'Payment Information'}
                {purchaseStep === 'success' && 'Purchase Complete'}
              </h3>
              <button
                onClick={closePurchaseModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* Purchase Details Step */}
            {purchaseStep === 'details' && (
              <div className="p-6">
                <div className="flex items-start space-x-4 mb-6">
                  <img
                    src={selectedItem.image}
                    alt={selectedItem.title}
                    className="w-24 h-16 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-gray-900 mb-1">
                      {selectedItem.title}
                    </h4>
                    <p className="text-sm text-gray-500 mb-2">by {selectedItem.author}</p>
                    <div className="flex items-center space-x-4 text-sm text-gray-500">
                      <div className="flex items-center space-x-1">
                        <i className="ri-star-fill text-yellow-400"></i>
                        <span>{selectedItem.rating}</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <i className="ri-download-line"></i>
                        <span>{selectedItem.downloads}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-2xl font-bold ${
                      selectedItem.pricing_model === 'free' ? 'text-green-600' : 'text-blue-600'
                    }`}>
                      {selectedItem.pricing_model === 'free' ? 'Free' : `$${selectedItem.price}`}
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h5 className="font-semibold text-gray-900 mb-2">Description</h5>
                  <p className="text-gray-600">{selectedItem.description}</p>
                </div>

                <div className="mb-6">
                  <h5 className="font-semibold text-gray-900 mb-3">What's Included</h5>
                  <ul className="space-y-2 text-gray-600">
                    <li className="flex items-center space-x-2">
                      <i className="ri-check-line text-green-500"></i>
                      <span>Full source code and documentation</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <i className="ri-check-line text-green-500"></i>
                      <span>6 months of free updates</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <i className="ri-check-line text-green-500"></i>
                      <span>Email support for 30 days</span>
                    </li>
                    <li className="flex items-center space-x-2">
                      <i className="ri-check-line text-green-500"></i>
                      <span>Commercial license included</span>
                    </li>
                  </ul>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                  <button
                    onClick={closePurchaseModal}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={selectedItem.pricing_model === 'free' ? completePurchase : proceedToPayment}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                  >
                    {selectedItem.pricing_model === 'free' ? 'Install Now' : 'Proceed to Payment'}
                  </button>
                </div>
              </div>
            )}

            {/* Payment Step */}
            {purchaseStep === 'payment' && (
              <div className="p-6">
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900">{selectedItem.title}</span>
                    <span className="font-bold text-blue-600">${selectedItem.price}</span>
                  </div>
                </div>

                <form className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="your@email.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Card Number
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="1234 5678 9012 3456"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Expiry Date
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="MM/YY"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CVC
                      </label>
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="123"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Billing Address
                    </label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2"
                      placeholder="Street Address"
                      required
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="City"
                        required
                      />
                      <input
                        type="text"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="ZIP Code"
                        required
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="terms"
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      required
                    />
                    <label htmlFor="terms" className="text-sm text-gray-600">
                      I agree to the <a href="#" className="text-blue-600 hover:underline">Terms of Service</a> and <a href="#" className="text-blue-600 hover:underline">Privacy Policy</a>
                    </label>
                  </div>
                </form>

                <div className="flex items-center justify-between pt-6 border-t border-gray-200 mt-6">
                  <button
                    onClick={() => setPurchaseStep('details')}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                  >
                    Back
                  </button>
                  <button
                    onClick={completePurchase}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                  >
                    Complete Purchase
                  </button>
                </div>
              </div>
            )}

            {/* Success Step */}
            {purchaseStep === 'success' && (
              <div className="p-6 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-2xl text-green-600"></i>
                </div>
                
                <h4 className="text-xl font-semibold text-gray-900 mb-2">
                  {selectedItem.pricing_model === 'free' ? 'Installation Complete!' : 'Purchase Successful!'}
                </h4>
                
                <p className="text-gray-600 mb-6">
                  {selectedItem.pricing_model === 'free' 
                    ? `${selectedItem.title} has been successfully installed to your account.`
                    : `Thank you for purchasing ${selectedItem.title}. You will receive a confirmation email shortly.`
                  }
                </p>

                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <h5 className="font-medium text-gray-900 mb-2">Next Steps:</h5>
                  <ul className="text-sm text-gray-600 space-y-1 text-left">
                    <li>• Download link sent to your email</li>
                    <li>• Access documentation and setup guide</li>
                    <li>• Join our community for support</li>
                    <li>• Rate and review this item</li>
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={closePurchaseModal}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
                  >
                    Continue Shopping
                  </button>
                  <button className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap">
                    View Downloads
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Call to Action */}
      <div className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Can't find what you're looking for?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Submit a request for custom development or suggest new marketplace items
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-blue-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors whitespace-nowrap">
              Request Custom Development
            </button>
            <button className="border border-gray-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors whitespace-nowrap">
              Suggest New Item
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <img 
                src="https://static.readdy.ai/image/feaac7b19917aec559683ef9ad3d4cd6/f01248d0a1f191b5d9d27b9480b2a552.jpeg" 
                alt="Allgo Digital Logo" 
                className="h-12 w-auto mb-4 filter brightness-0 invert"
              />
              <p className="text-gray-400">
                Transforming enterprise software development with integrated low-code, data flow, and BI solutions.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Platforms</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">Codee Platform</Link></li>
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">Fusion Platform</Link></li>
                <li><Link to="/platforms" className="hover:text-white cursor-pointer">BI Platform</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Resources</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/resources" className="hover:text-white cursor-pointer">Resources</Link></li>
                <li><Link to="/features" className="hover:text-white cursor-pointer">Features</Link></li>
                <li><Link to="/partners" className="hover:text-white cursor-pointer">Partner Program</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-gray-400">
                <li>info@allgo-digital.com</li>
                <li>+60 3-7931 8888</li>
                <li>Enterprise Solutions</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Allgo Digital Sdn Bhd. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MarketplacePage;
